import { useState, useEffect, type FormEvent } from 'react';
import { fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';

interface Props {
  email: string;
}

export default function ProfileForm({ email }: Props) {
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAttrs, setLoadingAttrs] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const attrs = await fetchUserAttributes();
        setFullName(attrs['custom:fullName'] ?? '');
        setCompany(attrs['custom:company'] ?? '');
        setPhone(attrs['custom:phone'] ?? '');
      } catch {
        // Attributes may not exist yet — that's fine
      } finally {
        setLoadingAttrs(false);
      }
    }
    void load();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:fullName': fullName,
          'custom:company': company,
          'custom:phone': phone,
        },
      });
      setSuccess('Profile updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  }

  if (loadingAttrs) {
    return <p className="settings-loading">Loading profile...</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="settings-email-display">
        <span className="field-label">Email</span>
        <span>{email}</span>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="profile-name">
          Full Name
        </label>
        <input
          id="profile-name"
          className="input"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Jane Doe"
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="profile-company">
          Company
        </label>
        <input
          id="profile-company"
          className="input"
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Acme Corp"
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="profile-phone">
          Phone
        </label>
        <input
          id="profile-phone"
          className="input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 555 123 4567"
        />
      </div>

      {success && <p className="success-msg">{success}</p>}
      {error && <p className="error-msg">{error}</p>}

      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
}
